import React, { useState, useEffect, useMemo } from 'react';
import { FiDatabase, FiInfo } from 'react-icons/fi';
import { getBlockStatusDetails, getBulkBlockStatusDetails } from '../services/api';
import { AttachmentFileList } from './StatusDetailAttachments';
import { blockLayerNumber } from '../utils/layerUtils';
import './BlockStatusDetailsTable.css';

function BlockStatusDetailsTable({ blocks, allBlocksForNumbering, onStatusUpdate, searchQuery = '' }) {
  const numberingSource = allBlocksForNumbering || blocks || [];

  const labelNum = (block) => blockLayerNumber(block, numberingSource);
  const [statusDetailsMap, setStatusDetailsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (blocks && blocks.length > 0) {
      fetchAllStatusDetails();
    } else {
      setLoading(false);
    }
  }, [blocks]);

  const fetchAllStatusDetails = async () => {
    try {
      setLoading(true);
      const detailsMap = {};
      
      // Fetch all status details in one bulk request (much faster!)
      const blockIds = blocks.map(block => block.id).join(',');
      try {
        const allDetails = await getBulkBlockStatusDetails(blockIds);
        
        // Group details by block ID
        allDetails.forEach(detail => {
          const blockId = detail.block_id;
          if (!detailsMap[blockId]) {
            detailsMap[blockId] = {};
          }
          detailsMap[blockId][detail.status_type] = detail;
        });
      } catch (error) {
        console.error('Error fetching bulk status details, falling back to individual requests:', error);
        // Fallback to individual requests if bulk endpoint fails
        const promises = blocks.map(async (block) => {
          try {
            const details = await getBlockStatusDetails(block.id);
            const detailsByType = {};
            details.forEach(detail => {
              detailsByType[detail.status_type] = detail;
            });
            return { blockId: block.id, details: detailsByType };
          } catch (error) {
            console.error(`Error fetching details for block ${block.id}:`, error);
            return { blockId: block.id, details: {} };
          }
        });

        const results = await Promise.all(promises);
        results.forEach(({ blockId, details }) => {
          detailsMap[blockId] = details;
        });
      }

      setStatusDetailsMap(detailsMap);
    } catch (error) {
      console.error('Error fetching status details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProcessingDeliverables = (processingDetail) => {
    if (!processingDetail || !processingDetail.checkboxes) return [];
    return processingDetail.checkboxes
      .filter(cb => cb.checked === 1)
      .map(cb => cb.checkbox_type);
  };

  // Filter blocks based on search query
  const filteredBlocks = useMemo(() => {
    if (!searchQuery.trim()) return blocks || [];
    
    const query = searchQuery.toLowerCase().trim();
    return (blocks || []).filter((block, index) => {
      const details = statusDetailsMap[block.id] || {};
      const gcp = details.gcp;
      const acquisition = details.acquisition;
      const processing = details.processing;
      const deliverables = getProcessingDeliverables(processing);
      
      // Search in block number
      if (`#${labelNum(block)}`.toLowerCase().includes(query)) return true;
      
      // Search in GCP details
      if (gcp) {
        if (
          (gcp.date && gcp.date.toLowerCase().includes(query)) ||
          (gcp.no_of_gcps && String(gcp.no_of_gcps).includes(query)) ||
          (gcp.crs && gcp.crs.toLowerCase().includes(query)) ||
          (gcp.surveyor && gcp.surveyor.toLowerCase().includes(query)) ||
          (gcp.device && gcp.device.toLowerCase().includes(query)) ||
          (gcp.remarks && gcp.remarks.toLowerCase().includes(query)) ||
          (gcp.attachments && gcp.attachments.some(a =>
            a.original_filename && a.original_filename.toLowerCase().includes(query)
          ))
        ) return true;
      }
      
      // Search in Acquisition details
      if (acquisition) {
        if (
          (acquisition.date && acquisition.date.toLowerCase().includes(query)) ||
          (acquisition.start_time && acquisition.start_time.toLowerCase().includes(query)) ||
          (acquisition.end_time && acquisition.end_time.toLowerCase().includes(query)) ||
          (acquisition.pilot && acquisition.pilot.toLowerCase().includes(query)) ||
          (acquisition.drone_used && acquisition.drone_used.toLowerCase().includes(query)) ||
          (acquisition.no_of_images && String(acquisition.no_of_images).includes(query)) ||
          (acquisition.gsd && acquisition.gsd.toLowerCase().includes(query)) ||
          (acquisition.remarks && acquisition.remarks.toLowerCase().includes(query)) ||
          (acquisition.attachments && acquisition.attachments.some(a =>
            a.original_filename && a.original_filename.toLowerCase().includes(query)
          ))
        ) return true;
      }
      
      // Search in Processing details
      if (processing) {
        if (
          (processing.date && processing.date.toLowerCase().includes(query)) ||
          (processing.crs && processing.crs.toLowerCase().includes(query)) ||
          (processing.gsd && processing.gsd.toLowerCase().includes(query)) ||
          (processing.remarks && processing.remarks.toLowerCase().includes(query)) ||
          (processing.attachments && processing.attachments.some(a =>
            a.original_filename && a.original_filename.toLowerCase().includes(query)
          )) ||
          deliverables.some(d => d.toLowerCase().includes(query))
        ) return true;
      }
      
      return false;
    });
  }, [blocks, searchQuery, statusDetailsMap]);

  if (loading) {
    return (
      <div className="block-status-details-table">
        <div className="loading-message">Loading status details...</div>
      </div>
    );
  }

  if (!blocks || blocks.length === 0) {
    return (
      <div className="block-status-details-table">
        <div className="no-blocks">
          <FiDatabase className="no-blocks-icon" />
          <p>No blocks available for this project</p>
        </div>
      </div>
    );
  }

  return (
    <div className="block-status-details-table">
      {searchQuery && (
        <div className="search-results-info">
          Showing {filteredBlocks.length} of {blocks?.length || 0} blocks
        </div>
      )}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Block</th>
              <th>GCPs Compilation</th>
              <th>Drone Acquisition</th>
              <th>Data Processing</th>
            </tr>
          </thead>
          <tbody>
            {filteredBlocks.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-results">
                  <div className="no-results-content">
                    <FiInfo className="no-results-icon" />
                    <p>No blocks found matching "{searchQuery}"</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredBlocks.map((block) => {
                const details = statusDetailsMap[block.id] || {};
              const gcp = details.gcp;
              const acquisition = details.acquisition;
              const processing = details.processing;
              const deliverables = getProcessingDeliverables(processing);

                return (
                  <tr key={block.id}>
                    <td className="block-number">#{labelNum(block)}</td>
                  
                  {/* GCPs Column */}
                  <td className="status-column gcp-column">
                    {gcp ? (
                      <div className="status-detail-content">
                        <div className="detail-row">
                          <span className="detail-label">Date:</span>
                          <span className="detail-value">{gcp.date || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">GCPs:</span>
                          <span className="detail-value">{gcp.no_of_gcps || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">CRS:</span>
                          <span className="detail-value">{gcp.crs || '-'}</span>
                        </div>
                        {gcp.surveyor && (
                          <div className="detail-row">
                            <span className="detail-label">Surveyor:</span>
                            <span className="detail-value">{gcp.surveyor}</span>
                          </div>
                        )}
                        {gcp.device && (
                          <div className="detail-row">
                            <span className="detail-label">Device:</span>
                            <span className="detail-value">{gcp.device}</span>
                          </div>
                        )}
                        <AttachmentFileList attachments={gcp.attachments} />
                        {gcp.remarks && (
                          <div className="detail-row remarks">
                            <span className="detail-label">Remarks:</span>
                            <span className="detail-value">{gcp.remarks}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="no-detail">
                        <FiInfo className="info-icon" />
                        <span>Not completed</span>
                      </div>
                    )}
                  </td>

                  {/* Acquisition Column */}
                  <td className="status-column acquisition-column">
                    {acquisition ? (
                      <div className="status-detail-content">
                        <div className="detail-row">
                          <span className="detail-label">Date:</span>
                          <span className="detail-value">{acquisition.date || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Time:</span>
                          <span className="detail-value">
                            {acquisition.start_time && acquisition.end_time 
                              ? `${acquisition.start_time} - ${acquisition.end_time}`
                              : '-'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Pilot:</span>
                          <span className="detail-value">{acquisition.pilot || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Drone:</span>
                          <span className="detail-value">{acquisition.drone_used || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Images:</span>
                          <span className="detail-value">{acquisition.no_of_images || '-'}</span>
                        </div>
                        {acquisition.gsd && (
                          <div className="detail-row">
                            <span className="detail-label">GSD:</span>
                            <span className="detail-value">{acquisition.gsd}</span>
                          </div>
                        )}
                        <AttachmentFileList attachments={acquisition.attachments} />
                        {acquisition.remarks && (
                          <div className="detail-row remarks">
                            <span className="detail-label">Remarks:</span>
                            <span className="detail-value">{acquisition.remarks}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="no-detail">
                        <FiInfo className="info-icon" />
                        <span>Not completed</span>
                      </div>
                    )}
                  </td>

                  {/* Processing Column */}
                  <td className="status-column processing-column">
                    {processing ? (
                      <div className="status-detail-content">
                        <div className="detail-row">
                          <span className="detail-label">Date:</span>
                          <span className="detail-value">{processing.date || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">CRS:</span>
                          <span className="detail-value">{processing.crs || '-'}</span>
                        </div>
                        {processing.gsd && (
                          <div className="detail-row">
                            <span className="detail-label">GSD:</span>
                            <span className="detail-value">{processing.gsd}</span>
                          </div>
                        )}
                        {deliverables.length > 0 && (
                          <div className="detail-row deliverables">
                            <span className="detail-label">Deliverables:</span>
                            <div className="deliverables-list">
                              {deliverables.map((deliverable, idx) => (
                                <span key={idx} className="deliverable-tag">{deliverable}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        <AttachmentFileList attachments={processing.attachments} />
                        {processing.remarks && (
                          <div className="detail-row remarks">
                            <span className="detail-label">Remarks:</span>
                            <span className="detail-value">{processing.remarks}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="no-detail">
                        <FiInfo className="info-icon" />
                        <span>Not completed</span>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BlockStatusDetailsTable;

